/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import api from './api';
import type { ClonedArtifactIdentity } from '../types/demand-source';
import type { ReqStrategy } from '../types';

/**
 * **For display only.** The defaults fill what a screen needs to render; they are never posted back.
 * A child strategy (`RQ10`) inherits whatever it does not state, and an array is one leaf, so a
 * defaulted `rounds: []` saved onto a child would be stored as its own value and cut it off from every
 * round its parent adds later. {@link reqStrategyService.update} writes over the stored document
 * instead (slice 7a).
 */
function normalizeStrategy(raw: Partial<ReqStrategy>): ReqStrategy {
  return {
    ...(raw as ReqStrategy),
    id: raw.id ?? '',
    name: raw.name ?? '',
    description: raw.description,
    enabled: raw.enabled ?? true,
    numCalls: raw.numCalls ?? 10,
    timing: raw.timing ?? 30,
    demandSources: raw.demandSources ?? [],
    rounds: raw.rounds ?? [],
    options: raw.options ?? {},
    addedBy: raw.addedBy,
    addedAt: raw.addedAt,
    updatedAt: raw.updatedAt,
  };
}

function assertUniqueId(id: string, existing: ReqStrategy[], currentId?: string): void {
  const hasDuplicate = existing.some((s) => s.id === id && s.id !== currentId);
  if (hasDuplicate) {
    throw new Error(`Strategy ID "${id}" is already in use.`);
  }
}

function assertUniqueName(name: string, existing: ReqStrategy[], currentId?: string): void {
  const lower = name.toLowerCase().trim();
  const hasDuplicate = existing.some((s) => s.name.toLowerCase().trim() === lower && s.id !== currentId);
  if (hasDuplicate) {
    throw new Error(`Strategy name "${name}" already exists.`);
  }
}

export const reqStrategyService = {
  async list(): Promise<ReqStrategy[]> {
    const { data } = await api.get<Partial<ReqStrategy>[]>('/api/req-strategies');
    return (data ?? []).map(normalizeStrategy);
  },

  /**
   * **One strategy, by id** (slice 3b) — what its detail page reads. It fetched the whole list and
   * searched it; the route asks the same access rule the list does and 404s what this viewer cannot see.
   */
  async get(id: string): Promise<ReqStrategy> {
    const { data } = await api.get<Partial<ReqStrategy>>(`/api/req-strategies/${encodeURIComponent(id)}`);
    return normalizeStrategy(data);
  },

  async create(strategy: Partial<ReqStrategy>, addedBy?: string): Promise<ReqStrategy> {
    // One id space per type, templates included: the list leaves them out (slice 7b), and a create over a
    // template's id would write over the template.
    const existing = [...(await reqStrategyService.list()), ...(await reqStrategyService.templates())];

    // **The id is authored, refused rather than minted** (A7, `#1147`/`#1148`). This fell back to
    // `generateUniqueId` — `slug-<epoch>` — which every editor for this type already makes
    // unreachable, having required an ID since before A7. That is what makes deleting it the point
    // rather than a tidy-up: *a fallback nobody reaches is how the surrogate would have survived the
    // migration meant to remove it*. A caller with no id now learns so instead of receiving one it
    // did not choose and cannot change.
    const id = strategy.id?.trim();
    if (!id) {
      throw new Error(
        'Strategy ID is required — it is how this is referenced elsewhere, and it cannot be changed afterwards.',
      );
    }

    assertUniqueId(id, existing);
    assertUniqueName(strategy.name ?? '', existing);

    const now = new Date().toISOString();
    const newStrategy: ReqStrategy = normalizeStrategy({
      ...strategy,
      id,
      addedBy: addedBy ?? strategy.addedBy,
      addedAt: now,
      updatedAt: now,
    });

    await api.put(`/api/req-strategies/${encodeURIComponent(newStrategy.id)}`, newStrategy);
    return newStrategy;
  },

  async update(id: string, strategy: Partial<ReqStrategy>, updatedBy?: string): Promise<ReqStrategy> {
    // **By id, never through the list** (2026-10-08). The list answers *what kind*, and leaves templates
    // out (slice 7b), so finding the target there meant a template could be opened and never saved: the
    // lookup threw before any request was sent. It is `3g`'s shape on demand sources, met by a strategy
    // template. The route answers 404 for what this viewer may not see.
    //
    // The document as the server sent it, too: the list's copy carries display defaults, and a child
    // would store each of them as its own value (see `normalizeStrategy`).
    const { data: stored } = await api.get<Partial<ReqStrategy>>(`/api/req-strategies/${encodeURIComponent(id)}`);
    // One id and name space per type, templates included, as `create` judges it.
    const existing = [...(await reqStrategyService.list()), ...(await reqStrategyService.templates())];

    const newId = strategy.id?.trim() ?? id;
    if (newId !== id) {
      assertUniqueId(newId, existing, id);
    }
    if (strategy.name && strategy.name !== stored.name) {
      assertUniqueName(strategy.name, existing, id);
    }

    const updated: Partial<ReqStrategy> = {
      ...stored,
      ...strategy,
      id: newId,
      updatedAt: new Date().toISOString(),
      addedBy: stored.addedBy ?? updatedBy,
      addedAt: stored.addedAt,
    };

    await api.put(`/api/req-strategies/${encodeURIComponent(newId)}`, updated);
    return normalizeStrategy(updated);
  },

  /**
   * **The request strategy templates this viewer may see** (`RQ10`, slice 7b) — the Templates page's
   * read. The strategy list leaves them out, for the reason the demand-source list does (`D31`).
   */
  async templates(): Promise<ReqStrategy[]> {
    const { data } = await api.get<Partial<ReqStrategy>[]>('/api/req-strategy-templates');
    return (data ?? []).map(normalizeStrategy);
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/api/req-strategies/${encodeURIComponent(id)}`);
  },

  /**
   * Copy a strategy — server-side, so the copy records where it came from.
   *
   * This used to be GET list → spread → new id → PUT, which recorded no relationship
   * at all, and **could not**: `origin` holds the source artifact's `object_id`, which
   * reaches no client until #1042. That is the one fact about a copy that cannot be
   * reconstructed once inheritance lands (#902), which is why it moved to the server.
   *
   * The id and name are chosen there too — uniqueness has to be judged against every
   * artifact of the type, and this list is filtered by visibility.
   */
  async clone(id: string, copy: ClonedArtifactIdentity): Promise<ReqStrategy> {
    const { data } = await api.post<ReqStrategy>(`/api/req-strategies/${encodeURIComponent(id)}/clone`, copy);
    return normalizeStrategy(data);
  },

  /**
   * Toggle the `enabled` state of a request strategy.
   */
  async setEnabled(id: string, enabled: boolean, updatedBy?: string): Promise<ReqStrategy> {
    return reqStrategyService.update(id, { enabled }, updatedBy);
  },

  /**
   * Update the `visibility` of a request strategy (admin-only).
   */
  async setVisibility(id: string, visibility: ReqStrategy['visibility'], updatedBy?: string): Promise<ReqStrategy> {
    return reqStrategyService.update(id, { visibility }, updatedBy);
  },
};
